import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Avatar,
  Tooltip,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Stack,
  Fade,
  LinearProgress,
  Autocomplete,
  FormGroup,
  Switch,
  Slider,
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  InputAdornment,
  Menu,
  Popover,
  Popper,
  ClickAwayListener,
  Grow,
  Zoom,
  Slide,
  Collapse,
  Skeleton,
  Backdrop,
  Snackbar,
  SnackbarContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  AdminPanelSettings as IGAIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Timeline as TimelineIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  BusinessCenter as BusinessCenterIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { igaAPI, applicationsAPI, usersAPI } from '../services/api';
import { instancesAPI } from '../services/instancesAPI';
import { IGAEntitlement, IGAEntitlementOwner, EntitlementMembership, CreateEntitlementRequest, UpdateEntitlementRequest } from '../types';
import { FORM_PLACEHOLDERS } from '../constants/formPlaceholders';
import { UI_TOKENS } from '../constants/uiTokens';
import { displayEntitlementRisk, resolveEntitlementRiskLevel } from '../utils/entitlementRisk';
import { entitlementPublishError, requestApprovalPreview } from '../utils/entitlementPublish';
import {
  getEnvironmentSurface,
  getLifecycleStatusSurface,
  getRiskSurface,
  getStatusSurface,
  chipSxFromSurface,
} from '../utils/appearanceTokens';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import CenteredLoader from '../components/CenteredLoader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`iga-tabpanel-${index}`}
      aria-labelledby={`iga-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// SailPoint Integration tab hidden from UI; kept for future re-enable.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SailPointIntegrationTab: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Application Discovery
    applicationName: '',
    applicationDescription: '',
    businessOwner: '',
    technicalOwner: '',
    applicationCategory: '',
    businessCriticality: '',
    dataClassification: '',
    complianceRequirements: [] as string[],
    
    // Application Type
    applicationType: '',
    
    // Connector Configuration
    connectorType: '',
    connectorConfig: {},
    
    // Technical Configuration
    endpoints: {
      baseUrl: '',
      authUrl: '',
      apiUrl: '',
      metadataUrl: '',
      callbackUrl: ''
    },
    authentication: {
      authType: '',
      clientId: '',
      clientSecret: '',
      username: '',
      password: '',
      certificate: '',
      privateKey: ''
    },
    connection: {
      timeout: 30,
      retryAttempts: 3,
      sslVerify: true,
      proxyEnabled: false,
      proxyUrl: '',
      proxyUsername: '',
      proxyPassword: ''
    },
    advanced: {
      customHeaders: [],
      customParameters: [],
      rateLimit: 100,
      batchSize: 50,
      syncInterval: 15
    },
    
    // Attribute Mapping
    attributeMappings: [],
    roleMappings: [],
    
    // Provisioning
    provisioningEnabled: false,
    syncFrequency: '',
    conflictResolution: '',
    
    // Security
    encryptionEnabled: false,
    auditLogging: false,
    dataRetention: ''
  });

  const steps = [
    'Application Discovery',
    'Application Type & Connector',
    'Technical Configuration',
    'Attribute Mapping',
    'Provisioning & Security',
    'Testing & Validation',
    'Review & Deploy'
  ];

  const applicationTypes = [
    { value: 'ad', label: 'Active Directory Based', icon: 'Domain', description: 'Applications using AD for authentication' },
    { value: 'cloud', label: 'Cloud Application (SaaS)', icon: 'Cloud', description: 'Software as a Service applications' },
    { value: 'web', label: 'Web Services/API', icon: 'API', description: 'REST/SOAP APIs and web services' },
    { value: 'database', label: 'Database Application', icon: 'Database', description: 'Database-driven applications' },
    { value: 'custom', label: 'Custom Application', icon: 'Code', description: 'Custom built applications' },
    { value: 'legacy', label: 'Legacy System', icon: 'Computer', description: 'Legacy or mainframe systems' }
  ];

  const ootbConnectors = [
    { value: 'salesforce', label: 'Salesforce', icon: 'Salesforce' },
    { value: 'servicenow', label: 'ServiceNow', icon: 'ServiceNow' },
    { value: 'workday', label: 'Workday', icon: 'Workday' },
    { value: 'office365', label: 'Office 365', icon: 'Microsoft' },
    { value: 'google', label: 'Google Workspace', icon: 'Google' },
    { value: 'aws', label: 'AWS', icon: 'AWS' },
    { value: 'azure', label: 'Azure', icon: 'Azure' },
    { value: 'box', label: 'Box', icon: 'Box' },
    { value: 'dropbox', label: 'Dropbox', icon: 'Dropbox' },
    { value: 'slack', label: 'Slack', icon: 'Slack' },
    { value: 'github', label: 'GitHub', icon: 'GitHub' },
    { value: 'jira', label: 'Jira', icon: 'Atlassian' },
    { value: 'confluence', label: 'Confluence', icon: 'Atlassian' },
    { value: 'zoom', label: 'Zoom', icon: 'Zoom' }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      applicationName: '',
      applicationDescription: '',
      businessOwner: '',
      technicalOwner: '',
      applicationCategory: '',
      businessCriticality: '',
      dataClassification: '',
      complianceRequirements: [],
      applicationType: '',
      connectorType: '',
      connectorConfig: {},
      endpoints: {
        baseUrl: '',
        authUrl: '',
        apiUrl: '',
        metadataUrl: '',
        callbackUrl: ''
      },
      authentication: {
        authType: '',
        clientId: '',
        clientSecret: '',
        username: '',
        password: '',
        certificate: '',
        privateKey: ''
      },
      connection: {
        timeout: 30,
        retryAttempts: 3,
        sslVerify: true,
        proxyEnabled: false,
        proxyUrl: '',
        proxyUsername: '',
        proxyPassword: ''
      },
      advanced: {
        customHeaders: [],
        customParameters: [],
        rateLimit: 100,
        batchSize: 50,
        syncInterval: 15
      },
      attributeMappings: [],
      roleMappings: [],
      provisioningEnabled: false,
      syncFrequency: '',
      conflictResolution: '',
      encryptionEnabled: false,
      auditLogging: false,
      dataRetention: ''
    });
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Application Discovery
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Application Name"
                  value={formData.applicationName}
                  onChange={(e) => setFormData({...formData, applicationName: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Business Owner</InputLabel>
                  <Select
                    value={formData.businessOwner}
                    onChange={(e) => setFormData({...formData, businessOwner: e.target.value})}
                  >
                    <MenuItem value="david.brown">David Brown</MenuItem>
                    <MenuItem value="lisa.rodriguez">Lisa Rodriguez</MenuItem>
                    <MenuItem value="michael.chen">Michael Chen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Application Description"
                  multiline
                  rows={3}
                  value={formData.applicationDescription}
                  onChange={(e) => setFormData({...formData, applicationDescription: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Application Category</InputLabel>
                  <Select
                    value={formData.applicationCategory}
                    onChange={(e) => setFormData({...formData, applicationCategory: e.target.value})}
                  >
                    <MenuItem value="enterprise">Enterprise Applications</MenuItem>
                    <MenuItem value="cloud">Cloud Applications</MenuItem>
                    <MenuItem value="custom">Custom Applications</MenuItem>
                    <MenuItem value="legacy">Legacy Systems</MenuItem>
                    <MenuItem value="database">Database Applications</MenuItem>
                    <MenuItem value="web">Web Applications</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Business Criticality</InputLabel>
                  <Select
                    value={formData.businessCriticality}
                    onChange={(e) => setFormData({...formData, businessCriticality: e.target.value})}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Compliance Requirements</InputLabel>
                  <Select
                    multiple
                    value={formData.complianceRequirements}
                    onChange={(e) => setFormData({...formData, complianceRequirements: e.target.value as string[]})}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="sox">SOX</MenuItem>
                    <MenuItem value="hipaa">HIPAA</MenuItem>
                    <MenuItem value="pci">PCI DSS</MenuItem>
                    <MenuItem value="gdpr">GDPR</MenuItem>
                    <MenuItem value="fedramp">FedRAMP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Application Type & Connector Selection
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Application Type</FormLabel>
                  <RadioGroup
                    value={formData.applicationType}
                    onChange={(e) => setFormData({...formData, applicationType: e.target.value})}
                  >
                    <Grid container spacing={2}>
                      {applicationTypes.map((type) => (
                        <Grid item xs={12} md={6} key={type.value}>
                          <Card 
                            sx={{ 
                              cursor: 'pointer',
                              border: formData.applicationType === type.value ? 2 : 1,
                              borderColor: formData.applicationType === type.value ? 'primary.main' : 'divider'
                            }}
                            onClick={() => setFormData({...formData, applicationType: type.value})}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Radio value={type.value} />
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {type.label}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {type.description}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>

              {formData.applicationType && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Connector Selection
                  </Typography>
                  {formData.applicationType === 'cloud' ? (
                    <Autocomplete
                      options={ootbConnectors}
                      getOptionLabel={(option) => option.label}
                      value={ootbConnectors.find(c => c.value === formData.connectorType) || null}
                      onChange={(event, newValue) => {
                        setFormData({...formData, connectorType: newValue?.value || ''});
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select OOTB Connector"
                          placeholder="Search for connectors..."
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{option.label}</Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Custom Connector Name"
                      value={formData.connectorType}
                      onChange={(e) => setFormData({...formData, connectorType: e.target.value})}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Technical Configuration
            </Typography>
            <Grid container spacing={3}>
              {/* Endpoints Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Endpoints Configuration
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Base URL"
                          value={formData.endpoints.baseUrl}
                          onChange={(e) => setFormData({
                            ...formData,
                            endpoints: { ...formData.endpoints, baseUrl: e.target.value }
                          })}
                          placeholder={FORM_PLACEHOLDERS.igaApiBaseUrl}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Authentication URL"
                          value={formData.endpoints.authUrl}
                          onChange={(e) => setFormData({
                            ...formData,
                            endpoints: { ...formData.endpoints, authUrl: e.target.value }
                          })}
                          placeholder={FORM_PLACEHOLDERS.igaTokenUrl}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="API URL"
                          value={formData.endpoints.apiUrl}
                          onChange={(e) => setFormData({
                            ...formData,
                            endpoints: { ...formData.endpoints, apiUrl: e.target.value }
                          })}
                          placeholder={FORM_PLACEHOLDERS.igaApiEndpoint}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Metadata URL"
                          value={formData.endpoints.metadataUrl}
                          onChange={(e) => setFormData({
                            ...formData,
                            endpoints: { ...formData.endpoints, metadataUrl: e.target.value }
                          })}
                          placeholder={FORM_PLACEHOLDERS.igaMetadataUrl}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Callback URL"
                          value={formData.endpoints.callbackUrl}
                          onChange={(e) => setFormData({
                            ...formData,
                            endpoints: { ...formData.endpoints, callbackUrl: e.target.value }
                          })}
                          placeholder={FORM_PLACEHOLDERS.igaCallbackUrl}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Authentication Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <VpnKeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Authentication Configuration
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Authentication Type</InputLabel>
                          <Select
                            value={formData.authentication.authType}
                            onChange={(e) => setFormData({
                              ...formData,
                              authentication: { ...formData.authentication, authType: e.target.value }
                            })}
                          >
                            <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                            <MenuItem value="basic">Basic Authentication</MenuItem>
                            <MenuItem value="apikey">API Key</MenuItem>
                            <MenuItem value="certificate">Certificate</MenuItem>
                            <MenuItem value="saml">SAML</MenuItem>
                            <MenuItem value="ldap">LDAP</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Client ID"
                          value={formData.authentication.clientId}
                          onChange={(e) => setFormData({
                            ...formData,
                            authentication: { ...formData.authentication, clientId: e.target.value }
                          })}
                          type="password"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Client Secret"
                          value={formData.authentication.clientSecret}
                          onChange={(e) => setFormData({
                            ...formData,
                            authentication: { ...formData.authentication, clientSecret: e.target.value }
                          })}
                          type="password"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={formData.authentication.username}
                          onChange={(e) => setFormData({
                            ...formData,
                            authentication: { ...formData.authentication, username: e.target.value }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Password"
                          value={formData.authentication.password}
                          onChange={(e) => setFormData({
                            ...formData,
                            authentication: { ...formData.authentication, password: e.target.value }
                          })}
                          type="password"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Certificate (PEM format)"
                          value={formData.authentication.certificate}
                          onChange={(e) => setFormData({
                            ...formData,
                            authentication: { ...formData.authentication, certificate: e.target.value }
                          })}
                          multiline
                          rows={3}
                          placeholder={FORM_PLACEHOLDERS.igaCertificatePem}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Connection Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Connection Settings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Timeout (seconds)"
                          type="number"
                          value={formData.connection.timeout}
                          onChange={(e) => setFormData({
                            ...formData,
                            connection: { ...formData.connection, timeout: parseInt(e.target.value) || 30 }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Retry Attempts"
                          type="number"
                          value={formData.connection.retryAttempts}
                          onChange={(e) => setFormData({
                            ...formData,
                            connection: { ...formData.connection, retryAttempts: parseInt(e.target.value) || 3 }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.connection.sslVerify}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection: { ...formData.connection, sslVerify: e.target.checked }
                              })}
                            />
                          }
                          label="Verify SSL Certificate"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.connection.proxyEnabled}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection: { ...formData.connection, proxyEnabled: e.target.checked }
                              })}
                            />
                          }
                          label="Enable Proxy"
                        />
                      </Grid>
                      {formData.connection.proxyEnabled && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Proxy URL"
                              value={formData.connection.proxyUrl}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection: { ...formData.connection, proxyUrl: e.target.value }
                              })}
                              placeholder={FORM_PLACEHOLDERS.igaProxyUrl}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Proxy Username"
                              value={formData.connection.proxyUsername}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection: { ...formData.connection, proxyUsername: e.target.value }
                              })}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Proxy Password"
                              value={formData.connection.proxyPassword}
                              onChange={(e) => setFormData({
                                ...formData,
                                connection: { ...formData.connection, proxyPassword: e.target.value }
                              })}
                              type="password"
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Advanced Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Advanced Settings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Rate Limit (requests/min)"
                          type="number"
                          value={formData.advanced.rateLimit}
                          onChange={(e) => setFormData({
                            ...formData,
                            advanced: { ...formData.advanced, rateLimit: parseInt(e.target.value) || 100 }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Batch Size"
                          type="number"
                          value={formData.advanced.batchSize}
                          onChange={(e) => setFormData({
                            ...formData,
                            advanced: { ...formData.advanced, batchSize: parseInt(e.target.value) || 50 }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Sync Interval (minutes)"
                          type="number"
                          value={formData.advanced.syncInterval}
                          onChange={(e) => setFormData({
                            ...formData,
                            advanced: { ...formData.advanced, syncInterval: parseInt(e.target.value) || 15 }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Custom Headers
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Header Name</TableCell>
                                <TableCell>Header Value</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <TextField size="small" placeholder="X-Custom-Header" />
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="custom-value" />
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small">
                                    <AddIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Attribute Mapping
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  User Attribute Mapping
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Source Attribute</TableCell>
                        <TableCell>Target Attribute</TableCell>
                        <TableCell>Data Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <TextField size="small" placeholder="e.g., email" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="e.g., mail" />
                        </TableCell>
                        <TableCell>
                          <Select size="small" defaultValue="string">
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Checkbox />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Provisioning & Security Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Provisioning Settings
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.provisioningEnabled}
                            onChange={(e) => setFormData({...formData, provisioningEnabled: e.target.checked})}
                          />
                        }
                        label="Enable Provisioning"
                      />
                    </FormGroup>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Sync Frequency</InputLabel>
                      <Select
                        value={formData.syncFrequency}
                        onChange={(e) => setFormData({...formData, syncFrequency: e.target.value})}
                      >
                        <MenuItem value="15min">Every 15 minutes</MenuItem>
                        <MenuItem value="1hour">Every hour</MenuItem>
                        <MenuItem value="4hours">Every 4 hours</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Security Settings
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.encryptionEnabled}
                            onChange={(e) => setFormData({...formData, encryptionEnabled: e.target.checked})}
                          />
                        }
                        label="Enable Encryption"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.auditLogging}
                            onChange={(e) => setFormData({...formData, auditLogging: e.target.checked})}
                          />
                        }
                        label="Enable Audit Logging"
                      />
                    </FormGroup>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Data Retention Policy</InputLabel>
                      <Select
                        value={formData.dataRetention}
                        onChange={(e) => setFormData({...formData, dataRetention: e.target.value})}
                      >
                        <MenuItem value="30days">30 days</MenuItem>
                        <MenuItem value="90days">90 days</MenuItem>
                        <MenuItem value="1year">1 year</MenuItem>
                        <MenuItem value="3years">3 years</MenuItem>
                        <MenuItem value="indefinite">Indefinite</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Testing & Validation
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Connection Testing
                    </Typography>
                    <Button variant="contained" startIcon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                      Test Connection
                    </Button>
                    <Alert severity="success">
                      Connection successful! All tests passed.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Data Validation
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Attribute mapping validated" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Connector configuration verified" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Security settings applied" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 6:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Deploy
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Configuration Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Application Name
                        </Typography>
                        <Typography variant="body1">{formData.applicationName}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Application Type
                        </Typography>
                        <Typography variant="body1">
                          {applicationTypes.find(t => t.value === formData.applicationType)?.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Connector Type
                        </Typography>
                        <Typography variant="body1">{formData.connectorType}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Business Criticality
                        </Typography>
                        <Typography variant="body1">{formData.businessCriticality}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={handleReset}>
                    Reset
                  </Button>
                  <Button variant="contained" color="primary">
                    Deploy to SailPoint
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
        SailPoint Integration
      </Typography>
      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 2 }}>
        Onboard your applications to SailPoint with automated connector configuration and attribute mapping.
      </Typography>

      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 1.5, borderColor: 'grey.200' }}>
        {renderStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          size="small"
          variant="outlined"
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ mr: 1, fontSize: UI_TOKENS.text.buttonSm }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleReset} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>
            Reset
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button size="small" variant="contained" onClick={handleReset} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>
              Finish
            </Button>
          ) : (
            <Button size="small" variant="contained" onClick={handleNext} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

type EntitlementRiskLevel = CreateEntitlementRequest['risk_level'];

const DEFAULT_DATA_CLASSIFICATION: CreateEntitlementRequest['data_classification'] = 'Internal';

const IGAOnboarding: React.FC = () => {
  const { user } = useAuth();
  const { colors, resolvedMode } = useTheme();
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [entitlements, setEntitlements] = useState<IGAEntitlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState<IGAEntitlement | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Ref to prevent duplicate API calls in React StrictMode
  const hasInitialLoad = useRef(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState<IGAEntitlement | null>(null);
  const [memberships, setMemberships] = useState<EntitlementMembership[]>([]);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const [approverDialogOpen, setApproverDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [requestPreviewOpen, setRequestPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRisk, setFilterRisk] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterEnvironment, setFilterEnvironment] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [availableApplications, setAvailableApplications] = useState<any[]>([]);
  const [availableInstances, setAvailableInstances] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form state for creating entitlements
  const [formData, setFormData] = useState<CreateEntitlementRequest & { environment?: string; application_id?: string; owner_approval_required?: boolean; security_approval_required?: boolean }>({
    application_id: '',
    instance_id: '',
    name: '',
    description: '',
    entitlement_type: 'Role',
    primary_approver_ids: [],
    secondary_approver_ids: [],
    entitlement_owners: [],
    risk_level: 'Medium',
    data_classification: DEFAULT_DATA_CLASSIFICATION,
    compliance_requirements: [],
    environment: 'Production',
    owner_approval_required: false,
    security_approval_required: false,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  /** Deep link: /applications/iga-onboarding?action=add opens the Create Entitlement dialog. */
  const addActionConsumedRef = useRef(false);
  useEffect(() => {
    if (addActionConsumedRef.current) return;
    if (searchParams.get('action') !== 'add') return;
    addActionConsumedRef.current = true;

    void (async () => {
      await loadAvailableUsers();
      setCreateDialogOpen(true);
    })();
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!user?.id) return;
    // Prevent duplicate API calls in React StrictMode
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      loadEntitlements();
      loadAvailableApplications();
      loadAvailableUsers();
    }
  }, [user?.id]);

  const loadEntitlements = async () => {
    try {
      console.log('🔍 loadEntitlements called');
      setLoading(true);
      const response = await igaAPI.getEntitlements();
      if (response.data.success && response.data.data) {
        setEntitlements(response.data.data);
      } else {
        setError('Failed to load entitlements');
      }
    } catch (err) {
      console.error('Error loading entitlements:', err);
      setError('Failed to load entitlements');
    } finally {
      setLoading(false);
    }
  };

  const isAdminRole = user?.role === 'super_admin' || user?.role === 'iga_admin' || user?.role === 'app_admin';

  const applicationRiskById = useMemo(
    () => new Map(availableApplications.map((app) => [app.id, app.risk_level as string | undefined])),
    [availableApplications],
  );

  const loadAvailableApplications = async () => {
    try {
      setLoadingApplications(true);
      const applications = await applicationsAPI.getApplications();
      const scoped = isAdminRole
        ? applications
        : applications.filter((app) => {
            const isOwner = app.primary_owners?.some((owner) => owner.user_id === user?.id)
              || app.secondary_owners?.some((owner) => owner.user_id === user?.id);
            const isDelegate = app.delegates?.some((delegate) => delegate.user_id === user?.id);
            return isOwner || isDelegate;
          });
      setAvailableApplications(scoped);
    } catch (err) {
      console.error('Error loading applications:', err);
      setAvailableApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  const loadAvailableInstances = async (applicationId: string) => {
    try {
      setLoadingInstances(true);
      const instances = await instancesAPI.getInstancesByApplication(applicationId);
      const selectedApp = availableApplications.find((app) => app.id === applicationId);
      const applicationName = selectedApp?.name || 'Unknown Application';
      setAvailableInstances(
        instances.map((instance) => ({
          id: instance.id,
          name: instance.name,
          environment: instance.environment,
          application_name: applicationName,
          application_id: applicationId,
        }))
      );
    } catch (err) {
      console.error('Error loading instances:', err);
      setAvailableInstances([]);
    } finally {
      setLoadingInstances(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await usersAPI.getUsersForPicker();
      setAvailableUsers(users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setAvailableUsers([]);
      setError('Failed to load users for entitlement form');
    } finally {
      setLoadingUsers(false);
    }
  };

  const openCreateDialog = async () => {
    await loadAvailableUsers();
    setCreateDialogOpen(true);
  };

  const extractApiError = (err: unknown, fallback: string) => {
    const axiosErr = err as { response?: { data?: { error?: string; message?: string } } };
    return axiosErr.response?.data?.error || axiosErr.response?.data?.message || fallback;
  };

  const validateEntitlementForm = (requireInstance: boolean) => {
    if (!formData.name?.trim()) {
      setError('Entitlement name is required');
      return false;
    }
    const publishError = entitlementPublishError(formData.description, formData.entitlement_owners);
    if (publishError) {
      setError(publishError);
      return false;
    }
    if (requireInstance && !formData.instance_id) {
      setError('Application and instance are required');
      return false;
    }
    return true;
  };

  const buildEntitlementPayload = () => ({
    instance_id: formData.instance_id,
    name: formData.name.trim(),
    description: formData.description.trim(),
    entitlement_type: formData.entitlement_type,
    primary_approver_ids: formData.primary_approver_ids || [],
    secondary_approver_ids: formData.secondary_approver_ids || [],
    entitlement_owners: formData.entitlement_owners || [],
    risk_level: formData.risk_level,
    data_classification: DEFAULT_DATA_CLASSIFICATION,
    compliance_requirements: formData.compliance_requirements || [],
    owner_approval_required: formData.owner_approval_required ?? false,
    security_approval_required: formData.security_approval_required ?? false,
  });

  // Filter entitlements based on search term and filters
  const filteredEntitlements = entitlements.filter((entitlement) => {
    const matchesSearch = searchTerm === '' || 
      entitlement.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entitlement.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entitlement.instance_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entitlement.application_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const displayRisk = displayEntitlementRisk(entitlement, applicationRiskById);
    const matchesRisk = filterRisk === 'all' || displayRisk === filterRisk;
    const matchesStatus = filterStatus === 'all' || entitlement.status === filterStatus;
    const matchesEnvironment = filterEnvironment === 'all' || (entitlement.environment || 'Production') === filterEnvironment;

    return matchesSearch && matchesRisk && matchesStatus && matchesEnvironment;
  });

  const totalFiltered = filteredEntitlements.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntitlements = sortListing(filteredEntitlements, listingSort).slice(startIndex, endIndex);

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterRisk('all');
    setFilterStatus('all');
    setFilterEnvironment('all');
    setCurrentPage(1);
  };

  const STAT_CARD_SX = {
    p: 1.5,
    textAlign: 'center' as const,
    borderRadius: 1.5,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  };
  const STAT_LABEL_SX = {
    color: '#666',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    mb: 0.5,
  };

  const handleDownloadEntitlementTemplate = async () => {
    try {
      setError(null);
      const blob = await igaAPI.downloadEntitlementTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'entitlements_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setImportSuccess('Template downloaded');
    } catch {
      setError('Failed to download entitlement template');
    }
  };

  const handleEntitlementFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setImportLoading(true);
      setImportProgress(0);
      setError(null);
      setImportSuccess(null);
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const fd = new FormData();
      fd.append('file', file);
      const result = await igaAPI.importEntitlements(fd);
      clearInterval(progressInterval);
      setImportProgress(100);

      if (result.successfulImports > 0) {
        setImportSuccess(
          `Import completed: ${result.successfulImports} entitlement(s) imported, ${result.failedImports} failed.`
        );
        await loadEntitlements();
      } else {
        setError('No entitlements were imported');
      }

      if (result.errors?.length > 0) {
        const errPreview = result.errors.slice(0, 5).join('; ');
        setError(
          result.errors.length <= 5
            ? `Import completed with issues: ${errPreview}`
            : `Import completed with ${result.errors.length} issues. First few: ${errPreview}`
        );
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; data?: { errors?: string[] } } };
      };
      const validationErrors = axiosErr.response?.data?.data?.errors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const preview = validationErrors.slice(0, 5).join('; ');
        setError(
          validationErrors.length <= 5
            ? `Validation failed: ${preview}`
            : `Validation failed (${validationErrors.length} errors). First few: ${preview}`
        );
      } else {
        setError(axiosErr.response?.data?.error || 'Failed to import entitlements');
      }
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      if (event.target) event.target.value = '';
    }
  };

  const handleCreateEntitlement = async () => {
    if (isCreating) {
      return;
    }
    if (!validateEntitlementForm(true)) {
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      const response = await igaAPI.createEntitlement(buildEntitlementPayload());
      if (response.data.success) {
        setCreateDialogOpen(false);
        setFormData({
          application_id: '',
          instance_id: '',
          name: '',
          description: '',
          entitlement_type: 'Role',
          primary_approver_ids: [],
          secondary_approver_ids: [],
          entitlement_owners: [],
          risk_level: 'Medium',
          data_classification: DEFAULT_DATA_CLASSIFICATION,
          compliance_requirements: [],
          environment: 'Production',
          owner_approval_required: false,
          security_approval_required: false,
        });
        setAvailableInstances([]);
        await loadEntitlements();
      } else {
        setError(response.data.error || 'Failed to create entitlement');
      }
    } catch (err) {
      console.error('Error creating entitlement:', err);
      setError(extractApiError(err, 'Failed to create entitlement'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditEntitlement = async (entitlement: IGAEntitlement) => {
    setEditingEntitlement(entitlement);
    const linkedApp = availableApplications.find((app) => app.id === entitlement.application_id);
    setFormData({
      application_id: entitlement.application_id,
      instance_id: entitlement.instance_id || '',
      name: entitlement.name,
      description: entitlement.description,
      entitlement_type: entitlement.entitlement_type,
      primary_approver_ids: (entitlement.primary_approvers || []).map((p) => p.user_id),
      secondary_approver_ids: (entitlement.secondary_approvers || []).map((s) => s.user_id),
      entitlement_owners: entitlement.entitlement_owners ? entitlement.entitlement_owners.map((owner) => owner.user_id) : [],
      risk_level: resolveEntitlementRiskLevel(linkedApp?.risk_level ?? entitlement.application_risk_level, entitlement.risk_level),
      data_classification: DEFAULT_DATA_CLASSIFICATION,
      compliance_requirements: entitlement.compliance_requirements,
      environment: entitlement.environment || 'Production',
      owner_approval_required: entitlement.owner_approval_required || false,
      security_approval_required: entitlement.security_approval_required || false,
    });
    await loadAvailableUsers();
    setEditDialogOpen(true);
  };

  const handleUpdateEntitlement = async () => {
    if (!editingEntitlement || isUpdating) return;
    if (!validateEntitlementForm(false)) {
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);
      const { application_id: _appId, instance_id: _instanceId, environment: _env, ...updateFields } = formData;
      const response = await igaAPI.updateEntitlement(editingEntitlement.id, {
        id: editingEntitlement.id,
        ...updateFields,
        name: formData.name.trim(),
        description: formData.description.trim(),
      });
      if (response.data.success) {
        setEditDialogOpen(false);
        setEditingEntitlement(null);
        setFormData({
          application_id: '',
          instance_id: '',
          name: '',
          description: '',
          entitlement_type: 'Role',
          primary_approver_ids: [],
          secondary_approver_ids: [],
          entitlement_owners: [],
          risk_level: 'Medium',
          data_classification: DEFAULT_DATA_CLASSIFICATION,
          compliance_requirements: [],
          environment: 'Production',
          owner_approval_required: false,
          security_approval_required: false,
        });
        await loadEntitlements();
      } else {
        setError(response.data.error || 'Failed to update entitlement');
      }
    } catch (err) {
      console.error('Error updating entitlement:', err);
      setError(extractApiError(err, 'Failed to update entitlement'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteEntitlement = async (entitlementId: string) => {
    if (!window.confirm('Are you sure you want to delete this entitlement?')) {
      return;
    }
    try {
      setError(null);
      const response = await igaAPI.deleteEntitlement(entitlementId);
      if (response.data.success) {
        await loadEntitlements();
      } else {
        setError(response.data.error || 'Failed to delete entitlement');
      }
    } catch (err) {
      console.error('Error deleting entitlement:', err);
      setError(extractApiError(err, 'Failed to delete entitlement'));
    }
  };

  const handleViewMemberships = async (entitlement: IGAEntitlement) => {
    try {
      const response = await igaAPI.getEntitlementMemberships(entitlement.id);
      if (response.data.success && response.data.data) {
        setMemberships(response.data.data);
        setSelectedEntitlement(entitlement);
        setMembershipDialogOpen(true);
      } else {
        setError('Failed to load memberships');
      }
    } catch (err) {
      console.error('Error loading memberships:', err);
      setError('Failed to load memberships');
    }
  };

  const getRiskChipStyle = (level: string | undefined) => {
    const surface = getRiskSurface(level || '', resolvedMode);
    return { bg: surface.bg, color: surface.fg, border: surface.border };
  };

  const getEnvChipStyle = (env: string) => {
    const surface =
      String(env || '').toLowerCase() === 'uat'
        ? getStatusSurface('purple', resolvedMode)
        : getEnvironmentSurface(env, resolvedMode);
    return { bg: surface.bg, color: surface.fg, border: surface.border };
  };

  const getEntitlementStatusStyle = (status: string) => {
    const surface = getLifecycleStatusSurface(status, resolvedMode);
    return { bg: surface.bg, fg: surface.fg, border: surface.border };
  };

  const TABLE_HEADER_SX = {
    fontWeight: 700,
    fontSize: UI_TOKENS.text.labelXs,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.4,
    color: 'text.secondary',
    py: 1,
    whiteSpace: 'nowrap' as const,
  };

  const ACCENT_COLORS = ['#1976d2', '#7b1fa2', '#00695c', '#c62828', '#e65100', '#1565c0', '#4a148c', '#00838f'];

  const DIALOG_FIELD_SX = {
    '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
    '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
    '& .MuiFormHelperText-root': { fontSize: UI_TOKENS.text.helper },
  };

  const DIALOG_SELECT_SX = {
    '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
    '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
  };

  const DIALOG_MENU_PROPS = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: UI_TOKENS.text.bodySm,
          minHeight: UI_TOKENS.menu.itemMinHeight,
          py: UI_TOKENS.menu.itemPaddingY,
        },
      },
    },
  };

  const renderDialogSectionHeader = (title: string, icon?: React.ReactNode) => (
    <Grid item xs={12} key={title}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {icon}
        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
    </Grid>
  );

  const renderUserAutocomplete = (
    label: string,
    placeholder: string,
    valueIds: string[],
    onChange: (ids: string[]) => void,
  ) => (
    <Autocomplete
      size="small"
      fullWidth
      multiple
      options={availableUsers}
      loading={loadingUsers}
      getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.username})`}
      value={availableUsers.filter((user) => valueIds.includes(user.id))}
      onChange={(_, newValue) => onChange(newValue.map((user) => user.id))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={
            !loadingUsers && availableUsers.length === 0
              ? 'No users available. Contact your administrator if this persists.'
              : undefined
          }
          sx={DIALOG_FIELD_SX}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Box>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{option.first_name} {option.last_name}</Typography>
            <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary' }}>{option.username}</Typography>
          </Box>
        </li>
      )}
    />
  );

  const userNames = (ids: string[] | undefined) =>
    (ids || [])
      .map((id) => availableUsers.find((user) => user.id === id))
      .filter(Boolean)
      .map((user) => `${user.first_name} ${user.last_name}`.trim());

  const renderEntitlementFormFields = (includeAppInstance: boolean) => (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="Entitlement Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          sx={DIALOG_FIELD_SX}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={2}
          required
          helperText="At least 12 characters. Placeholder text such as lorem ipsum or TBD cannot be saved."
          sx={DIALOG_FIELD_SX}
        />
      </Grid>

      {includeAppInstance && (
        <>
          {renderDialogSectionHeader('Application & Instance', <ComputerIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={availableApplications}
              loading={loadingApplications}
              getOptionLabel={(option) => option.name}
              value={availableApplications.find((app) => app.id === formData.application_id) || null}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  application_id: newValue?.id || '',
                  instance_id: '',
                  environment: 'Production',
                  risk_level: resolveEntitlementRiskLevel(newValue?.risk_level),
                });
                if (newValue?.id) {
                  loadAvailableInstances(newValue.id);
                } else {
                  setAvailableInstances([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Application"
                  required
                  placeholder="Search applications…"
                  helperText="Select an application you own or are delegated to"
                  sx={DIALOG_FIELD_SX}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingApplications ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{option.name}</Typography>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={availableInstances}
              loading={loadingInstances}
              disabled={!formData.application_id}
              getOptionLabel={(option) => option.name}
              value={availableInstances.find((instance) => instance.id === formData.instance_id) || null}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  instance_id: newValue?.id || '',
                  environment: newValue?.environment || formData.environment,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Instance"
                  required
                  placeholder="Search instances…"
                  helperText="Select an instance from the chosen application"
                  sx={DIALOG_FIELD_SX}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingInstances ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{option.name}</Typography>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              label="Environment"
              value={formData.environment || 'Production'}
              InputProps={{ readOnly: true }}
              helperText="Set automatically from the selected instance"
              sx={DIALOG_FIELD_SX}
            />
          </Grid>
        </>
      )}

      {renderDialogSectionHeader('Risk', <SecurityIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={6}>
        <TextField
          size="small"
          fullWidth
          label="Risk Level"
          value={formData.risk_level || 'Medium'}
          InputProps={{ readOnly: true }}
          helperText={
            formData.application_id
              ? 'Inherited from the selected application'
              : 'Select an application to set risk level'
          }
          sx={DIALOG_FIELD_SX}
        />
      </Grid>

      {renderDialogSectionHeader('Approvers & Owners', <PeopleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={6}>
        {renderUserAutocomplete('Entitlement Owners', 'Search users…', formData.entitlement_owners, (ids) =>
          setFormData({ ...formData, entitlement_owners: ids })
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderUserAutocomplete('Primary Approvers', 'Search users…', formData.primary_approver_ids, (ids) =>
          setFormData({ ...formData, primary_approver_ids: ids })
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderUserAutocomplete('Secondary Approvers', 'Search users…', formData.secondary_approver_ids, (ids) =>
          setFormData({ ...formData, secondary_approver_ids: ids })
        )}
      </Grid>

      {renderDialogSectionHeader('Approval Requirements', <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={formData.owner_approval_required || false}
              onChange={(e) => setFormData({ ...formData, owner_approval_required: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Owner Approval Required</Typography>
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                Requires approval from application owners before granting access
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={formData.security_approval_required || false}
              onChange={(e) => setFormData({ ...formData, security_approval_required: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Security Approval Required</Typography>
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                Requires approval from security team before granting access
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          {requestApprovalPreview({
            name: formData.name,
            ownerNames: userNames(formData.entitlement_owners),
            primaryNames: userNames(formData.primary_approver_ids),
            secondaryNames: userNames(formData.secondary_approver_ids),
            memberNames: [],
            ownerApproval: Boolean(formData.owner_approval_required),
            securityApproval: Boolean(formData.security_approval_required),
          })}
        </Alert>
      </Grid>
    </Grid>
  );

  const renderDialogActions = (
    onCancel: () => void,
    onSubmit: () => void,
    submitLabel: string,
    submitting: boolean,
  ) => (
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button size="small" onClick={onCancel} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
        Cancel
      </Button>
      <Button
        size="small"
        onClick={onSubmit}
        variant="contained"
        disabled={submitting}
        sx={{ fontSize: UI_TOKENS.text.bodySm }}
      >
        {submitting ? <CircularProgress size={16} /> : submitLabel}
      </Button>
    </DialogActions>
  );

  const renderDialogTitle = (title: string, onClose: () => void) => (
    <>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IGAIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{title}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </DialogTitle>
      <Divider />
    </>
  );

  const renderEntitlementRow = (entitlement: IGAEntitlement) => {
    const envStyle = getEnvChipStyle(entitlement.environment || 'Production');
    const displayRisk = displayEntitlementRisk(entitlement, applicationRiskById);
    const riskStyle = getRiskChipStyle(displayRisk);
    const statusStyle = getEntitlementStatusStyle(entitlement.status);

    return (
      <TableRow key={entitlement.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
        <TableCell sx={{ py: 0.75, px: 1 }}>
          <Tooltip title={entitlement.name}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.bodySm, color: 'text.primary' }}>
              {entitlement.name}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ py: 0.75, px: 1 }}>
          <Tooltip title={entitlement.description || ''}>
          <Typography
            noWrap
            sx={{
              fontSize: UI_TOKENS.text.bodySm,
              color: 'text.secondary',
              cursor: entitlement.description && entitlement.description.length > 80 ? 'pointer' : 'default',
              '&:hover': entitlement.description && entitlement.description.length > 80 ? { textDecoration: 'underline' } : {},
            }}
            onClick={() => {
              if (entitlement.description && entitlement.description.length > 80) {
                setSelectedEntitlement(entitlement);
                setDescriptionDialogOpen(true);
              }
            }}
          >
            {entitlement.description && entitlement.description.length > 80
              ? `${entitlement.description.substring(0, 80)}...`
              : (entitlement.description || '—')}
          </Typography>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 500 }} noWrap>
            {entitlement.application_name || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }} noWrap>
            {entitlement.instance_name || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Chip
            label={entitlement.environment || 'Production'}
            size="small"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: envStyle.bg, color: envStyle.color, '& .MuiChip-label': { px: 1 } }}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Chip
            label={entitlement.status}
            size="small"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: statusStyle.bg, color: statusStyle.fg, '& .MuiChip-label': { px: 1 } }}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Chip
            label={displayRisk}
            size="small"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: riskStyle.bg, color: riskStyle.color, '& .MuiChip-label': { px: 1 } }}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Tooltip title="View owners">
            <IconButton
              size="small"
              onClick={() => { setSelectedEntitlement(entitlement); setOwnerDialogOpen(true); }}
              sx={{ p: 0.5, color: 'primary.main' }}
            >
              <AccountCircleIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Chip
            label={entitlement.owner_approval_required ? 'Required' : 'Not Required'}
            size="small"
            sx={{
              height: 20,
              fontSize: UI_TOKENS.text.helper,
              fontWeight: 600,
              ...chipSxFromSurface(
                getStatusSurface(entitlement.owner_approval_required ? 'success' : 'neutral', resolvedMode)
              ),
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Chip
            label={entitlement.security_approval_required ? 'Required' : 'Not Required'}
            size="small"
            sx={{
              height: 20,
              fontSize: UI_TOKENS.text.helper,
              fontWeight: 600,
              ...chipSxFromSurface(
                getStatusSurface(entitlement.security_approval_required ? 'success' : 'neutral', resolvedMode)
              ),
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <Tooltip title="Request preview">
            <IconButton
              size="small"
              onClick={() => { setSelectedEntitlement(entitlement); setRequestPreviewOpen(true); }}
              sx={{ p: 0.5, color: 'primary.main' }}
            >
              <ViewIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View members">
            <IconButton
              size="small"
              onClick={() => { setSelectedEntitlement(entitlement); setMembersDialogOpen(true); }}
              sx={{ p: 0.5, color: 'primary.main' }}
            >
              <PeopleIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Tooltip title="Edit">
              <IconButton size="small" color="info" onClick={() => handleEditEntitlement(entitlement)} sx={{ p: 0.5 }}>
                <EditIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDeleteEntitlement(entitlement.id)} sx={{ p: 0.5 }}>
                <DeleteIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderEntitlementCard = (entitlement: IGAEntitlement, cardIndex: number) => {
    const accentColor = ACCENT_COLORS[cardIndex % ACCENT_COLORS.length];
    const envStyle = getEnvChipStyle(entitlement.environment || 'Production');
    const displayRisk = displayEntitlementRisk(entitlement, applicationRiskById);
    const riskStyle = getRiskChipStyle(displayRisk);
    const statusStyle = getEntitlementStatusStyle(entitlement.status);

    return (
      <Grid item xs={12} md={6} lg={4} key={entitlement.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${accentColor}`,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
          }}
        >
          <CardContent sx={{ p: 0, pb: '0 !important', flexGrow: 1 }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: `${accentColor}08`, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: accentColor, lineHeight: 1.2 }}>
                  {entitlement.name}
                </Typography>
                <Chip
                  label={displayRisk}
                  size="small"
                  sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: riskStyle.bg, color: riskStyle.color, flexShrink: 0 }}
                />
              </Box>
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.5 }} noWrap>
                {entitlement.application_name} · {entitlement.instance_name}
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {entitlement.description || 'No description'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                <Chip label={entitlement.environment || 'Production'} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: envStyle.bg, color: envStyle.color }} />
                <Chip label={entitlement.status} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, bgcolor: statusStyle.bg, color: statusStyle.fg }} />
                <Chip label={`${entitlement.member_count || 0} members`} size="small" variant="outlined" sx={{ height: 20, fontSize: UI_TOKENS.text.helper, '& .MuiChip-label': { px: 1 } }} />
              </Box>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 2, py: 1.5, pt: 0, borderTop: '1px solid', borderColor: 'divider', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />}
              onClick={() => handleViewMemberships(entitlement)}
              sx={{ fontSize: UI_TOKENS.text.labelXs, height: 22, px: 1, py: 0, borderRadius: 1.5 }}
            >
              Members
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setSelectedEntitlement(entitlement); setRequestPreviewOpen(true); }}
              sx={{ fontSize: UI_TOKENS.text.labelXs, height: 22, px: 1, py: 0, borderRadius: 1.5 }}
            >
              Preview
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />}
              onClick={() => handleEditEntitlement(entitlement)}
              sx={{ fontSize: UI_TOKENS.text.labelXs, height: 22, px: 1, py: 0, borderRadius: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />}
              onClick={() => handleDeleteEntitlement(entitlement.id)}
              sx={{ fontSize: UI_TOKENS.text.labelXs, height: 22, px: 1, py: 0, borderRadius: 1.5 }}
            >
              Delete
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return <CenteredLoader />;
  }

  const hasActiveFilters = searchTerm !== '' || filterRisk !== 'all' || filterStatus !== 'all' || filterEnvironment !== 'all';

  return (
    <Box sx={{ p: 3 }}>
      {/* Main Header — matches Applications Management */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IGAIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
              IGA Onboarding
            </Typography>
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
              {entitlements.length} entitlement{entitlements.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="table" aria-label="table view">
              <TableViewIcon sx={{ fontSize: '0.9rem' }} />
            </ToggleButton>
            <ToggleButton value="card" aria-label="card view">
              <CardViewIcon sx={{ fontSize: '0.9rem' }} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={handleDownloadEntitlementTemplate}
            sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
          >
            Template
          </Button>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={<UploadIcon sx={{ fontSize: '0.9rem !important' }} />}
            disabled={importLoading}
            sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
          >
            Bulk Import
            <input type="file" hidden accept=".xlsx,.xls" onChange={handleEntitlementFileUpload} />
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={loadEntitlements}
            disabled={loading}
            sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => void openCreateDialog()}
            sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
          >
            Create Entitlement
          </Button>
        </Box>
      </Box>

      {importLoading && (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                Importing entitlements… {importProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={importProgress} sx={{ height: 4, borderRadius: 2 }} />
          </CardContent>
        </Card>
      )}

      {importSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSuccess(null)}>
          {importSuccess}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards — compact style matching Applications */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={STAT_CARD_SX}>
              <Typography variant="body2" sx={STAT_LABEL_SX}>TOTAL ENTITLEMENTS</Typography>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1.8rem', mb: 0.25 }}>
                {entitlements.length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', fontSize: UI_TOKENS.text.helper }}>
                Entitlements in system
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={STAT_CARD_SX}>
              <Typography variant="body2" sx={STAT_LABEL_SX}>ACTIVE</Typography>
              <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 700, fontSize: '1.8rem', mb: 0.25 }}>
                {entitlements.filter((e) => e.status === 'Active').length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', fontSize: UI_TOKENS.text.helper }}>
                Active entitlements
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={STAT_CARD_SX}>
              <Typography variant="body2" sx={STAT_LABEL_SX}>PENDING</Typography>
              <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 700, fontSize: '1.8rem', mb: 0.25 }}>
                {entitlements.filter((e) => e.status === 'Pending').length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', fontSize: UI_TOKENS.text.helper }}>
                Pending review
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={STAT_CARD_SX}>
              <Typography variant="body2" sx={STAT_LABEL_SX}>HIGH RISK</Typography>
              <Typography variant="h5" sx={{ color: '#c62828', fontWeight: 700, fontSize: '1.8rem', mb: 0.25 }}>
                {entitlements.filter((e) => {
                  const risk = displayEntitlementRisk(e, applicationRiskById);
                  return risk === 'High' || risk === 'Critical';
                }).length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', fontSize: UI_TOKENS.text.helper }}>
                High or critical risk
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filters & Search — matches Applications */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600, mb: 1 }}>
              Filters & Search
            </Typography>
            {(() => {
              const INPUT_SX = {
                '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
                '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
                '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
              };
              const MENU_PROPS = {
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: UI_TOKENS.text.bodySm,
                      minHeight: UI_TOKENS.menu.itemMinHeight,
                      py: UI_TOKENS.menu.itemPaddingY,
                    },
                  },
                },
              };
              return (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Search Entitlements"
                      placeholder="Search by name, description, application, or instance"
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      size="small"
                      sx={INPUT_SX}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small" sx={INPUT_SX}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                        label="Status"
                        MenuProps={MENU_PROPS}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small" sx={INPUT_SX}>
                      <InputLabel>Risk Level</InputLabel>
                      <Select
                        value={filterRisk}
                        onChange={(e) => { setFilterRisk(e.target.value); setCurrentPage(1); }}
                        label="Risk Level"
                        MenuProps={MENU_PROPS}
                      >
                        <MenuItem value="all">All Risk Levels</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small" sx={INPUT_SX}>
                      <InputLabel>Environment</InputLabel>
                      <Select
                        value={filterEnvironment}
                        onChange={(e) => { setFilterEnvironment(e.target.value); setCurrentPage(1); }}
                        label="Environment"
                        MenuProps={MENU_PROPS}
                      >
                        <MenuItem value="all">All Environments</MenuItem>
                        <MenuItem value="Production">Production</MenuItem>
                        <MenuItem value="Staging">Staging</MenuItem>
                        <MenuItem value="Development">Development</MenuItem>
                        <MenuItem value="UAT">UAT</MenuItem>
                        <MenuItem value="Testing">Testing</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <ListingSortSelect
                      value={listingSort}
                      onChange={(value) => { setListingSort(value); setCurrentPage(1); }}
                      sx={INPUT_SX}
                      menuProps={MENU_PROPS}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearAllFilters}
                      sx={{ fontSize: UI_TOKENS.text.buttonSm, mt: 0.5 }}
                    >
                      Clear All Filters
                    </Button>
                  </Grid>
                </Grid>
              );
            })()}
          </CardContent>
        </Card>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm }} color="text.secondary">
            {totalFiltered} entitlement{totalFiltered !== 1 ? 's' : ''}
            {hasActiveFilters ? ' (filtered)' : ''}
          </Typography>
        </Box>

        {totalFiltered === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
              {entitlements.length === 0 ? 'No entitlements found' : 'No entitlements match the current filters'}
            </Typography>
            {entitlements.length === 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                onClick={() => void openCreateDialog()}
                sx={{ mt: 2, fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
              >
                Create Your First Entitlement
              </Button>
            )}
          </Box>
        ) : viewMode === 'table' ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', maxHeight: '70vh', overflowX: 'auto' }}>
            <Table size="small" sx={{ ...listingTableSx, width: '100%', minWidth: 1292, tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  {[
                    ['Entitlement', 160],
                    ['Description', 180],
                    ['Application', 110],
                    ['Instance', 100],
                    ['Environment', 108],
                    ['Status', 84],
                    ['Risk', 80],
                    ['Owners', 56],
                    ['Owner Approval', 124],
                    ['Security Approval', 132],
                    ['Members', 88],
                    ['Actions', 76],
                  ].map(([label, width]) => (
                    <TableCell
                      key={label}
                      sx={{ ...TABLE_HEADER_SX, width, px: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      align={label === 'Actions' ? 'right' : 'left'}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntitlements.map((entitlement) => renderEntitlementRow(entitlement))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Grid container spacing={2}>
            {paginatedEntitlements.map((entitlement, idx) => renderEntitlementCard(entitlement, idx))}
          </Grid>
        )}

        <ListingPagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={totalFiltered}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          pageSizeSelectSx={{
            '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
            '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
            '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
          }}
          pageSizeMenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: UI_TOKENS.text.bodySm,
                  minHeight: UI_TOKENS.menu.itemMinHeight,
                  py: UI_TOKENS.menu.itemPaddingY,
                },
              },
            },
          }}
        />
      {/* Create Entitlement Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        {renderDialogTitle('Create New Entitlement', () => setCreateDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          {renderEntitlementFormFields(true)}
        </DialogContent>
        {renderDialogActions(
          () => setCreateDialogOpen(false),
          handleCreateEntitlement,
          'Create Entitlement',
          isCreating,
        )}
      </Dialog>

      {/* Edit Entitlement Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        {renderDialogTitle('Edit Entitlement', () => setEditDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          {renderEntitlementFormFields(false)}
        </DialogContent>
        {renderDialogActions(
          () => setEditDialogOpen(false),
          handleUpdateEntitlement,
          'Update Entitlement',
          isUpdating,
        )}
      </Dialog>

      {/* Membership Dialog */}
      <Dialog open={membershipDialogOpen} onClose={() => setMembershipDialogOpen(false)} maxWidth="lg" fullWidth>
        {renderDialogTitle(`Entitlement Members — ${selectedEntitlement?.name || ''}`, () => setMembershipDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, borderColor: 'grey.200' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  {['User', 'Department', 'Job Title', 'Manager', 'Status', 'Access Granted', 'Last Accessed'].map((label) => (
                    <TableCell key={label} sx={TABLE_HEADER_SX}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {memberships.map((membership) => (
                  <TableRow key={membership.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                          {membership.first_name?.[0]}{membership.last_name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {membership.first_name} {membership.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {membership.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{membership.department}</TableCell>
                    <TableCell>{membership.job_title}</TableCell>
                    <TableCell>{membership.manager}</TableCell>
                    <TableCell>
                      <Chip
                        label={membership.status}
                        color={membership.status === 'Active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(membership.access_granted_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {membership.last_accessed 
                        ? new Date(membership.last_accessed).toLocaleDateString()
                        : 'Never'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setMembershipDialogOpen(false)} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Owners Dialog */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="md" fullWidth>
        {renderDialogTitle(`Entitlement Owners — ${selectedEntitlement?.name || ''}`, () => setOwnerDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          {selectedEntitlement?.entitlement_owners && selectedEntitlement.entitlement_owners.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, borderColor: 'grey.200' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    {['Name', 'Email', 'Username', 'Added Date'].map((label) => (
                      <TableCell key={label} sx={TABLE_HEADER_SX}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEntitlement.entitlement_owners.map((owner: any) => (
                    <TableRow key={owner.id || owner.user_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {owner.first_name?.[0]}{owner.last_name?.[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {owner.first_name} {owner.last_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {owner.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {owner.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {owner.created_at ? new Date(owner.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
                No owners assigned to this entitlement yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOwnerDialogOpen(false)} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={descriptionDialogOpen} onClose={() => setDescriptionDialogOpen(false)} maxWidth="sm" fullWidth>
        {renderDialogTitle(`Description — ${selectedEntitlement?.name || ''}`, () => setDescriptionDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {selectedEntitlement?.description || 'No description available'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDescriptionDialogOpen(false)} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={requestPreviewOpen} onClose={() => setRequestPreviewOpen(false)} maxWidth="sm" fullWidth>
        {renderDialogTitle(`Request preview — ${selectedEntitlement?.name || ''}`, () => setRequestPreviewOpen(false))}
        <DialogContent>
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, mt: 1 }}>
            {selectedEntitlement
              ? requestApprovalPreview({
                  name: selectedEntitlement.name,
                  ownerNames: (selectedEntitlement.entitlement_owners || []).map((owner) => `${owner.first_name} ${owner.last_name}`.trim()),
                  primaryNames: (selectedEntitlement.primary_approvers || []).map((approver) => `${approver.first_name} ${approver.last_name}`.trim()),
                  secondaryNames: (selectedEntitlement.secondary_approvers || []).map((approver) => `${approver.first_name} ${approver.last_name}`.trim()),
                  memberNames: (selectedEntitlement.memberships || []).map((member) => `${member.first_name} ${member.last_name}`.trim()),
                  memberCount: selectedEntitlement.member_count,
                  ownerApproval: Boolean(selectedEntitlement.owner_approval_required),
                  securityApproval: Boolean(selectedEntitlement.security_approval_required),
                })
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setRequestPreviewOpen(false)} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={membersDialogOpen} onClose={() => setMembersDialogOpen(false)} maxWidth="md" fullWidth>
        {renderDialogTitle(`Entitlement Members — ${selectedEntitlement?.name || ''}`, () => setMembersDialogOpen(false))}
        <DialogContent sx={{ pt: 2 }}>
          {selectedEntitlement?.memberships && selectedEntitlement.memberships.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, borderColor: 'grey.200' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    {['Name', 'Email', 'Department', 'Job Title', 'Status', 'Access Granted'].map((label) => (
                      <TableCell key={label} sx={TABLE_HEADER_SX}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEntitlement.memberships.map((member: any) => (
                    <TableRow key={member.id || member.user_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {member.first_name} {member.last_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {member.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {member.department || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {member.job_title || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={member.status || 'Active'} 
                          size="small" 
                          color={member.status === 'Active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {member.access_granted_date 
                            ? new Date(member.access_granted_date).toLocaleDateString() 
                            : 'N/A'
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
                No members assigned to this entitlement yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setMembersDialogOpen(false)} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default IGAOnboarding;